import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { isNotNullable } from 'utils/ts';

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;

export interface UrlObject {
  src: string;
  type: 'image' | 'video';
}

export const VIDEO_FILE_EXTENSIONS = [
  /* 'webm', */
  '.gif',
  '.mp4',
  '.m4p',
  '.m4v',
  '.mpg',
  '.mp2',
  '.mpeg',
  '.mpe',
  '.mpv',
  '.m2v',
  '.ogv',
  '.3gp',
  '.3g2',
  '.avi',
] as const;

export const IMAGE_FILE_EXTENSIONS = ['.tif', '.tiff', '.jpeg', '.jpg', '.png', '.svg', '.bmp', '.webp'] as const;

/**
 * Attempt to identify the file type based on various file extensions
 */
export const getUrlFileType = (url: string): 'image' | 'video' | '' => {
  if (IMAGE_FILE_EXTENSIONS.some((ext) => url.includes(ext))) {
    return 'image';
  }
  if (VIDEO_FILE_EXTENSIONS.some((ext) => url.includes(ext))) {
    return 'video';
  }
  return '';
};

/**
 * Parse multiple lines of urls and identify the file type
 */

export const parseMultilineUrl = (urlText: string): UrlObject[] => {
  return (
    urlText
      .split('\n')
      .map((url) => {
        const type = getUrlFileType(url);

        if (type) {
          return {
            src: url,
            type,
          };
        }
        return null;
      })
      // only image and video types are allowed
      .filter(isNotNullable)
  );
};
export const extractMetadata = async (file: File): Promise<{ fps: number; framesCount: number }> => {
  const ffmpeg = new FFmpeg();

  try {
    // Load FFmpeg.wasm-core.js and FFmpeg.wasm
    await ffmpeg.load({
      coreURL: '/ffmpeg/ffmpeg-core.js',
      wasmURL: '/ffmpeg/ffmpeg-core.wasm',
    });

    // Convert the file to a Uint8Array and write it to the FFmpeg file system
    const data = await fetchFile(file);
    await ffmpeg.writeFile(file.name, data);

    // Capture log output from FFmpeg
    let ffmpegOutput = '';
    ffmpeg.on('log', ({ message }) => {
      ffmpegOutput += `${message}\n`;
    });

    // Execute ffmpeg to process the input file without generating an output file
    // -i        : Specifies the input file
    // file.name : The name of the input file being processed
    // -f        : Specifies the output format
    // 'null'    : A special format that discards the output, meaning no file is created
    // '/dev/null': On Unix-like systems, this is a "null device" where any data written to it is discarded
    await ffmpeg.exec(['-i', file.name, '-f', 'null', '/dev/null']);

    const fps = parseFps(ffmpegOutput);
    const framesCount = parseFramesCount(ffmpegOutput, fps);
    return { fps, framesCount };
  } catch (error) {
    console.error('Error during FFmpeg operation:', error);
    throw error;
  } finally {
    await cleanupFile(ffmpeg, file.name);
  }
};

const parseFps = (ffmpegOutput: string): number => {
  const fpsMatch = ffmpegOutput.match(/(\d+(\.\d+)?) fps/);
  return fpsMatch ? Number.parseFloat(fpsMatch[1]) : 0;
};

const parseFramesCount = (ffmpegOutput: string, fps: number): number => {
  const durationMatch = ffmpegOutput.match(/Duration: (\d+):(\d+):(\d+\.\d+)/);

  if (!durationMatch) return 0;

  const hours = Number.parseInt(durationMatch[1], 10);
  const minutes = Number.parseInt(durationMatch[2], 10);
  const seconds = Number.parseFloat(durationMatch[3]);

  const totalSeconds = hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
  return Math.round(totalSeconds * fps);
};

const cleanupFile = async (ffmpeg: FFmpeg, fileName: string): Promise<void> => {
  try {
    await ffmpeg.deleteFile(fileName);
  } catch (error) {
    console.error('Error during cleanup:', error);
  }
};
